import type { QueryClient } from '@tanstack/react-query';
import { api, ApiError } from './client';

/**
 * End the signed-in session for real: POST /auth/logout revokes it on the server
 * and clears the httpOnly cookie. Clearing only the client cache left the cookie
 * valid, so visiting the app signed the person straight back in.
 *
 * Resolves `true` when the session is over — the server ended it, or it was
 * already dead (401) — and the tab's session storage (e.g. the room viewer's
 * remembered email) is cleared too. Resolves `false` when the call failed any
 * other way (network drop, 5xx): the cookie may still be valid, so the caller
 * must NOT show the person a signed-out screen. The query cache is cleared
 * either way.
 */
export async function endSession(qc: Pick<QueryClient, 'clear'>): Promise<boolean> {
  let ended = true;
  try {
    await api.post<void>('/auth/logout');
  } catch (err) {
    ended = err instanceof ApiError && err.status === 401;
  } finally {
    qc.clear();
  }
  if (ended) {
    try {
      sessionStorage.clear();
    } catch {
      // Storage unavailable (private mode): nothing was stored there either.
    }
  }
  return ended;
}

/** Shown when sign-out could not reach the server. */
export const SIGN_OUT_FAILED_MESSAGE =
  "Couldn't sign you out. Check your connection and try again.";
